/**
 * Viewport 3D: textura WebGL2 (DVR/MIP) con fallback MIP CPU.
 * Por defecto proyecta el volumen entero; el decapado es opcional.
 */
import React, { Component } from 'react'
import { projectMip } from '../utils/mpr3dVolume'
import { createGlVolumeRenderer } from '../utils/mpr3dWebgl'
import { buildClinicalTf, buildRegionTf, MPR3D_REGION_PALETTES, MPR3D_COLOR_STYLES, mpr3dPresetLabel } from '../utils/mpr3dPresets'
import Mpr3dMeshOverlay from './Mpr3dMeshOverlay'
import Mpr3dLayersPanel from './Mpr3dLayersPanel'
import { isPhoneLikeDevice } from '../utils/mobileDownloadProfile'
import { vol3dOrientationLabels } from '../utils/mpr3dLaterality'
import { loadMpr3dSession, saveMpr3dSession } from '../utils/mpr3dSession'
import {
  mpr3dGridToWorld,
  MPR3D_DEFAULT_PITCH,
  MPR3D_DEFAULT_YAW,
  MPR3D_DEFAULT_ZOOM,
  MPR3D_PAN_MAX,
  wrapAnglePi,
  MPR3D_ZOOM_MAX,
  MPR3D_ZOOM_MIN,
} from '../utils/mpr3dCamera'

const CLIP_AXES = [
  { id: 'z', label: 'Axial' },
  { id: 'y', label: 'Coronal' },
  { id: 'x', label: 'Sagital' },
]

const VIEW_TOOLS = [
  { id: 'orbit', label: 'Orbitar' },
  { id: 'pan', label: 'Mover' },
  { id: 'zoom', label: 'Zoom' },
  { id: 'wwwc', label: 'WW/WC' },
  { id: 'peel', label: 'Decapar' },
]

/** Detalle clínico: unsharp sobre el volumen (grano de parénquima). */
const DETAIL_FILTERS = [
  { id: 'soft', label: 'Suave', amount: 0 },
  { id: 'sharp', label: 'Sharp', amount: 1.15 },
  { id: 'grain', label: 'Grano', amount: 2.05 },
]

/** Modo de apariencia 3D: clínico ≈ MPR; presentación = modelo sombreado. */
const LOOK_MODES = [
  { id: 'clinical', label: 'Clínico' },
  { id: 'present', label: 'Presentación' },
]

/** Ángulo de luz en espacio de vista (x=right, y=up, z=hacia cámara). */
const LIGHT_PRESETS = [
  { id: 'front', label: 'Frontal', view: { x: 0.05, y: 0.22, z: 1 } },
  { id: 'ne', label: '↗', view: { x: 0.55, y: 0.75, z: 0.5 } },
  { id: 'nw', label: '↖', view: { x: -0.55, y: 0.75, z: 0.5 } },
  { id: 'side', label: 'Lateral', view: { x: 0.95, y: 0.28, z: 0.25 } },
]

function clipMaskForAxis(axis, on) {
  if (!on) return { x: 0, y: 0, z: 0 }
  return {
    x: axis === 'x' ? 1 : 0,
    y: axis === 'y' ? 1 : 0,
    z: axis === 'z' ? 1 : 0,
  }
}

function keepVec(sign) {
  const k = sign < 0 ? -1 : 1
  return { x: k, y: k, z: k }
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v))
}

const chipStyle = (active, extra) => ({
  border: '1px solid rgba(255,255,255,0.28)',
  borderRadius: 14,
  padding: '4px 10px',
  fontSize: 11,
  letterSpacing: 0.2,
  color: '#eee',
  background: extra || (active ? 'rgba(200, 90, 40, 0.78)' : 'rgba(20, 20, 20, 0.72)'),
  cursor: 'pointer',
  touchAction: 'manipulation',
})

export default class Mpr3dViewport extends Component {
  constructor(props) {
    super(props)
    this.glCanvasRef = React.createRef()
    this.cpuCanvasRef = React.createRef()
    this.wrapRef = React.createRef()
    this.meshRef = React.createRef()
    this.yaw = MPR3D_DEFAULT_YAW
    this.pitch = MPR3D_DEFAULT_PITCH
    this.zoom = MPR3D_DEFAULT_ZOOM
    this.panX = 0
    this.panY = 0
    this.mirrorX = false
    this.mirrorY = false
    this._drag = null
    this._pinch = null
    this._raf = 0
    this._dirty = true
    this._fast = false
    this._idleTimer = null
    this._ro = null
    this._glr = null
    this._backend = null
    this._shading = 'dvr'
    this._tool = 'orbit'
    this._palette = props.palette || 'general'
    this._colorStyle = props.colorStyle || 'anatomico'
    this._lookMode = 'present'
    this._lightId = 'ne'
    this._detailId = 'soft'
    this._lastTap = 0
    this._holdTimer = null
    this._segMode = 'off'
    this.state = {
      backend: null,
      shading: 'dvr',
      tool: 'orbit',
      palette: this._palette,
      colorStyle: this._colorStyle,
      uiHidden: true,
      lookMode: 'present',
      lightId: 'ne',
      detailId: 'soft',
      layersOpen: false,
    }
  }

  componentDidMount() {
    this.bind()
    if (typeof ResizeObserver !== 'undefined' && this.wrapRef.current) {
      this._ro = new ResizeObserver(() => this.scheduleDraw(this._fast))
      this._ro.observe(this.wrapRef.current)
    }
    window.addEventListener('resize', this.onResize)
    window.addEventListener('orientationchange', this.onResize)
    window.addEventListener('keydown', this.onKeyDown)
    const ses = loadMpr3dSession()
    if (ses) {
      if (ses.lookMode) this.setLookMode(ses.lookMode)
      if (ses.lightId) this.setLightPreset(ses.lightId)
      if (ses.detailId) this.setDetailFilter(ses.detailId)
      if (ses.uiHidden === false) this.setState({ uiHidden: false })
    }
    // No compilar WebGL hasta que haya volumen: el overlay debe pintarse ya.
    if (this.props.volume && this.props.volume.data) {
      this._glInitTimer = setTimeout(() => {
        this._glInitTimer = null
        this.initRenderer()
        this.scheduleDraw(true)
      }, 0)
    }
  }

  componentDidUpdate(prev) {
    if (
      (prev.volume !== this.props.volume || prev.volumeGen !== this.props.volumeGen) &&
      this.props.volume &&
      this.props.volume.data
    ) {
      if (!this._glr) {
        this.initRenderer()
      }
      if (this._glr) {
        const ok = this._glr.upload(this.props.volume)
        if (!ok) this.fallbackCpu()
        else if (this._glr.setTf) this.refreshTf()
      }
      this.scheduleDraw(true)
      this.markFastThenIdle()
    }
    if (prev.palette !== this.props.palette && this.props.palette) {
      this.applyPalette(this.props.palette, this.props.colorStyle || this._colorStyle)
    }
    if (prev.colorStyle !== this.props.colorStyle && this.props.colorStyle) {
      this.applyColorStyle(this.props.colorStyle)
    }
    if (
      prev.volume !== this.props.volume ||
      prev.clip !== this.props.clip ||
      prev.mode !== this.props.mode ||
      prev.decapado !== this.props.decapado ||
      prev.clipAxis !== this.props.clipAxis ||
      prev.keepSign !== this.props.keepSign ||
      prev.slab !== this.props.slab ||
      prev.lut !== this.props.lut ||
      prev.palette !== this.props.palette ||
      prev.colorStyle !== this.props.colorStyle ||
      prev.mark !== this.props.mark ||
      prev.markOn !== this.props.markOn ||
      prev.viewHintGen !== this.props.viewHintGen ||
      prev.volumeGen !== this.props.volumeGen ||
      prev.ww !== this.props.ww ||
      prev.wc !== this.props.wc ||
      prev.building !== this.props.building ||
      prev.mask !== this.props.mask ||
      prev.maskOn !== this.props.maskOn ||
      prev.segMode !== this.props.segMode ||
      prev.volOff !== this.props.volOff ||
      prev.wwwcLink !== this.props.wwwcLink
    ) {
      this.scheduleDraw(false)
    }
    if (this._glr && this._glr.uploadMask && (prev.mask !== this.props.mask || prev.volumeGen !== this.props.volumeGen)) {
      const vol = this.props.volume
      this._glr.uploadMask(this.props.mask, vol && vol.size)
    }
    if (prev.isolateGen !== this.props.isolateGen && this.props.isolateBox) {
      this.fitToRoi(this.props.isolateBox)
    }
    if (prev.viewHintGen !== this.props.viewHintGen && this.props.viewHint) {
      this.orientToPlane(this.props.viewHint)
      // Punto mira → modo clínico (parecido al MPR) + WW/WC del MPR activo.
      this.setLookMode('clinical', { syncWwwc: true })
    }
    if (prev.decapado !== this.props.decapado) {
      if (!this.props.decapado && this._tool === 'peel') {
        this._tool = 'orbit'
        this.setState({ tool: 'orbit' })
      }
    }
  }

  componentWillUnmount() {
    this.unbind()
    if (this._glInitTimer) {
      clearTimeout(this._glInitTimer)
      this._glInitTimer = null
    }
    if (this._raf) cancelAnimationFrame(this._raf)
    if (this._idleTimer) clearTimeout(this._idleTimer)
    if (this._ro) {
      try { this._ro.disconnect() } catch (e) { /* ignore */ }
    }
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('orientationchange', this.onResize)
    window.removeEventListener('keydown', this.onKeyDown)
    if (this._holdTimer) clearTimeout(this._holdTimer)
    this.disposeGl()
  }

  initRenderer = () => {
    const canvas = this.glCanvasRef.current
    if (!canvas) {
      this._backend = 'cpu'
      this._shading = 'mip'
      this.setState({ backend: 'cpu', shading: 'mip' })
      return
    }
    this.disposeGl()
    const glr = createGlVolumeRenderer(canvas)
    if (glr) {
      if (this.props.volume) glr.upload(this.props.volume)
      this._glr = glr
      this._backend = 'gl'
      this.refreshTf()
      this.setState({ backend: 'gl' })
    } else {
      this._backend = 'cpu'
      this._shading = 'mip'
      this.setState({ backend: 'cpu', shading: 'mip' })
    }
  }

  disposeGl = () => {
    if (this._glr) {
      try { this._glr.dispose() } catch (e) { /* ignore */ }
    }
    this._glr = null
  }

  fallbackCpu = () => {
    if (this._backend === 'cpu') return
    this.disposeGl()
    this._backend = 'cpu'
    this._shading = 'mip'
    this.setState({ backend: 'cpu', shading: 'mip' }, () => this.scheduleDraw(false))
  }

  bind = () => {
    const wrap = this.wrapRef.current
    if (!wrap) return
    // Gestos en el contenedor (no solo canvas): mouse y touch fiables con la herramienta activa.
    wrap.addEventListener('pointerdown', this.onPointerDown)
    wrap.addEventListener('pointermove', this.onPointerMove)
    wrap.addEventListener('pointerup', this.onPointerUp)
    wrap.addEventListener('pointercancel', this.onPointerUp)
    wrap.addEventListener('lostpointercapture', this.onPointerUp)
    wrap.addEventListener('wheel', this.onWheel, { passive: false })
    wrap.addEventListener('touchstart', this.onTouchStart, { passive: false })
    wrap.addEventListener('touchmove', this.onTouchMove, { passive: false })
    wrap.addEventListener('touchend', this.onTouchEnd)
  }

  unbind = () => {
    const wrap = this.wrapRef.current
    if (!wrap) return
    wrap.removeEventListener('pointerdown', this.onPointerDown)
    wrap.removeEventListener('pointermove', this.onPointerMove)
    wrap.removeEventListener('pointerup', this.onPointerUp)
    wrap.removeEventListener('pointercancel', this.onPointerUp)
    wrap.removeEventListener('lostpointercapture', this.onPointerUp)
    wrap.removeEventListener('wheel', this.onWheel)
    wrap.removeEventListener('touchstart', this.onTouchStart)
    wrap.removeEventListener('touchmove', this.onTouchMove)
    wrap.removeEventListener('touchend', this.onTouchEnd)
  }

  onResize = () => {
    this.scheduleDraw(false)
  }

  scheduleDraw = (fast) => {
    this._fast = !!fast
    this._dirty = true
    if (this._raf) return
    this._raf = requestAnimationFrame(this.draw)
  }

  draw = () => {
    this._raf = 0
    if (!this._dirty) return
    this._dirty = false
    const wrap = this.wrapRef.current
    const volume = this.props.volume
    if (this.meshRef.current && this.meshRef.current.paint) this.meshRef.current.paint()
    if (!wrap || !volume || !volume.data) return

    const rect = wrap.getBoundingClientRect()
    const cssW = Math.max(64, Math.floor(rect.width || 256))
    const cssH = Math.max(64, Math.floor(rect.height || 256))
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const phone = isPhoneLikeDevice()

    if (this._backend === 'gl' && this._glr && !this._glr.lost) {
      try {
        const okUpload = this._glr.upload(volume)
        if (!okUpload) {
          this.fallbackCpu()
          return
        }
        const okDraw = this._glr.draw({
          width: cssW,
          height: cssH,
          dpr,
          yaw: this.yaw,
          pitch: this.pitch,
          zoom: this.zoom,
          panX: this.panX,
          panY: this.panY,
          mode: this._shading === 'mip' ? 'mip' : 'dvr',
          shadeMode: this._lookMode === 'clinical' ? 'clinical' : 'present',
          lightView: (LIGHT_PRESETS.find((p) => p.id === this._lightId) || LIGHT_PRESETS[1]).view,
          clip: this.props.clip,
          keep: keepVec(this.props.keepSign),
          clipMask: clipMaskForAxis(this.props.clipAxis || 'z', !!this.props.decapado),
          decapado: !!this.props.decapado,
          slab: Number(this.props.slab) || 0,
          mark: this.props.mark,
          markOn: !!this.props.markOn,
          fast: this._fast,
          phone,
          ww: this.props.ww,
          wc: this.props.wc,
          maskOn: !!this.props.maskOn && this.props.segMode !== 'mesh',
          volOff: this.props.segMode === 'mesh' || !!this.props.volOff,
          flipX: !!this.mirrorX,
          flipY: !!this.mirrorY,
          sharp:
            this._lookMode === 'clinical'
              ? (DETAIL_FILTERS.find((d) => d.id === this._detailId) || DETAIL_FILTERS[0]).amount
              : 0,
        })
        if (!okDraw) this.fallbackCpu()
      } catch (e) {
        this.fallbackCpu()
      }
      return
    }

    const maxOut = 288
    const cpuScale = Math.min(0.75, maxOut / Math.max(cssW, cssH, 1))
    const outW = Math.max(64, Math.round(cssW * cpuScale))
    const outH = Math.max(64, Math.round(cssH * cpuScale))

    let img
    try {
      img = projectMip(volume, {
        width: outW,
        height: outH,
        yaw: this.yaw,
        pitch: this.pitch,
        zoom: this.zoom,
        panX: this.panX,
        panY: this.panY,
        mode: 'mip',
        clip: this.props.clip,
        keep: keepVec(this.props.keepSign),
        clipMask: clipMaskForAxis(this.props.clipAxis || 'z', !!this.props.decapado),
        decapado: !!this.props.decapado,
        slab: Number(this.props.slab) || 0,
        lut: this.props.lut || 'bone',
        fast: this._fast,
        flipX: !!this.mirrorX,
        flipY: !!this.mirrorY,
      })
    } catch (e) {
      if (this.props.onError) this.props.onError(e)
      return
    }
    if (!img) return

    const canvas = this.cpuCanvasRef.current
    if (!canvas) return
    canvas.width = img.width
    canvas.height = img.height
    canvas.style.width = `${cssW}px`
    canvas.style.height = `${cssH}px`
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const imageData = new ImageData(img.pixels, img.width, img.height)
    ctx.putImageData(imageData, 0, 0)
  }

  markFastThenIdle = () => {
    this.scheduleDraw(true)
    if (this._idleTimer) clearTimeout(this._idleTimer)
    this._idleTimer = setTimeout(() => {
      this._idleTimer = null
      this.scheduleDraw(false)
    }, 140)
  }

  isUiTarget = (e) => {
    const t = e.target
    return !!(t && t.closest && t.closest('[data-mpr3d-ui]'))
  }

  onPointerDown = (e) => {
    if (this.isUiTarget(e)) return
    if (e.pointerType === 'touch' && e.isPrimary === false) return
    if (this._pinch) return
    if (e.button != null && e.button !== 0 && e.pointerType === 'mouse') return
    e.preventDefault()
    e.stopPropagation()
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch (err) { /* ignore */ }
    const wrap = this.wrapRef.current
    const rect = wrap ? wrap.getBoundingClientRect() : { width: 256, height: 256 }
    const axis = this.props.clipAxis || 'z'
    const clip = this.props.clip || {}
    const mode = this.dragMode(e)
    this._drag = {
      mode,
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      yaw: this.yaw,
      pitch: this.pitch,
      zoom: this.zoom,
      panX: this.panX,
      panY: this.panY,
      clip0: Number(clip[axis]) || 0,
      ww0: Number(this.props.ww) > 0 ? Number(this.props.ww) : 400,
      wc0: Number.isFinite(Number(this.props.wc)) ? Number(this.props.wc) : 40,
      w: Math.max(40, rect.width),
      h: Math.max(40, rect.height),
      moved: false,
    }
    if (this._holdTimer) clearTimeout(this._holdTimer)
    if (e.pointerType === 'touch' && mode === 'orbit') {
      this._holdTimer = setTimeout(() => {
        this._holdTimer = null
        if (!this._drag || this._drag.moved) return
        this.setTool('wwwc')
      }, 480)
    }
  }

  dragMode = (e) => {
    // Con herramienta WW/WC activa, el arrastre siempre ajusta ventana
    // (salvo rueda central / shift = pan, como atajo).
    const tool = this._tool || this.state.tool || 'orbit'
    if (tool === 'wwwc') {
      if (e.shiftKey || e.button === 1 || e.buttons === 4) return 'pan'
      return 'wwwc'
    }
    if (e.shiftKey || e.button === 1 || e.buttons === 4) return 'pan'
    if (e.altKey) return 'zoom'
    if (tool === 'pan' || tool === 'zoom' || tool === 'peel') return tool
    return 'orbit'
  }

  onPointerMove = (e) => {
    if (!this._drag || this._pinch) return
    if (this._drag.pointerId != null && e.pointerId !== this._drag.pointerId) return
    e.preventDefault()
    e.stopPropagation()
    const dx = e.clientX - this._drag.x
    const dy = e.clientY - this._drag.y
    if (Math.abs(dx) + Math.abs(dy) > 6) this._drag.moved = true
    if (this._drag.moved && this._holdTimer) {
      clearTimeout(this._holdTimer)
      this._holdTimer = null
    }
    const mode = this._drag.mode || 'orbit'
    const sx = this.mirrorX ? -1 : 1
    const sy = this.mirrorY ? -1 : 1
    if (mode === 'pan') {
      this.panX = clamp(this._drag.panX + sx * dx / this._drag.w * 1.7, -MPR3D_PAN_MAX, MPR3D_PAN_MAX)
      this.panY = clamp(this._drag.panY - sy * dy / this._drag.h * 1.7, -MPR3D_PAN_MAX, MPR3D_PAN_MAX)
    } else if (mode === 'zoom') {
      this.zoom = clamp(this._drag.zoom * Math.exp(-dy * 0.006), MPR3D_ZOOM_MIN, MPR3D_ZOOM_MAX)
    } else if (mode === 'peel') {
      const n1 = Math.max(1, ((this.props.volume && this.props.volume.size) || 2) - 1)
      const next = this._drag.clip0 - (dy / this._drag.h) * n1
      if (this.props.onClipTo) this.props.onClipTo(next)
    } else if (mode === 'wwwc') {
      if (!this.props.onWwwc) return
      // Como MPR: horizontal = WC, vertical = WW.
      const ww0 = this._drag.ww0 || 400
      const wc0 = this._drag.wc0 || 40
      const wwScale = Math.max(80, ww0 * 1.6)
      const nextWw = clamp(Math.round(ww0 + (-dy / this._drag.h) * wwScale), 1, 4000)
      const nextWc = clamp(Math.round(wc0 + (dx / this._drag.w) * wwScale * 0.9), -1000, 3000)
      this.props.onWwwc(nextWw, nextWc)
      return
    } else {
      this.yaw = wrapAnglePi(this._drag.yaw - sx * dx * 0.007)
      this.pitch = wrapAnglePi(this._drag.pitch + sy * dy * 0.006)
    }
    this.markFastThenIdle()
  }

  onPointerUp = (e) => {
    if (this._drag && e && e.pointerId != null && this._drag.pointerId != null && e.pointerId !== this._drag.pointerId) {
      return
    }
    if (this._drag && e && e.currentTarget && this._drag.pointerId != null) {
      try { e.currentTarget.releasePointerCapture(this._drag.pointerId) } catch (err) { /* ignore */ }
    }
    if (this._holdTimer) {
      clearTimeout(this._holdTimer)
      this._holdTimer = null
    }
    const drag = this._drag
    this._drag = null
    this.scheduleDraw(false)
    if (drag && !drag.moved && this.props.onToggleMaximize) {
      const now = Date.now()
      if (now - this._lastTap < 320) {
        this._lastTap = 0
        this.props.onToggleMaximize()
      } else {
        this._lastTap = now
      }
    }
  }

  onWheel = (e) => {
    if (this.isUiTarget(e) && !this.props.decapado && this._tool !== 'wwwc') return
    e.preventDefault()
    if ((this._tool || this.state.tool) === 'wwwc' && this.props.onWwwc) {
      const ww = Number(this.props.ww) > 0 ? Number(this.props.ww) : 400
      const wc = Number.isFinite(Number(this.props.wc)) ? Number(this.props.wc) : 40
      const step = Math.max(5, Math.round(ww * 0.04))
      this.props.onWwwc(clamp(ww + (e.deltaY > 0 ? step : -step), 1, 4000), wc)
      return
    }
    const peel = this.props.decapado && this.props.onClipDelta && !e.ctrlKey && !e.shiftKey && !e.metaKey && this._tool !== 'zoom'
    if (peel) {
      const n1 = Math.max(1, ((this.props.volume && this.props.volume.size) || 2) - 1)
      this.props.onClipDelta(-(e.deltaY / 120) * n1 * 0.03)
      return
    }
    const f = e.deltaY > 0 ? 0.92 : 1.08
    this.zoom = clamp(this.zoom * f, MPR3D_ZOOM_MIN, MPR3D_ZOOM_MAX)
    this.markFastThenIdle()
  }

  onTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault()
      this._drag = null
      const a = e.touches[0]
      const b = e.touches[1]
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
      this._pinch = {
        dist: d || 1,
        zoom: this.zoom,
        panX: this.panX,
        panY: this.panY,
        cx: (a.clientX + b.clientX) * 0.5,
        cy: (a.clientY + b.clientY) * 0.5,
      }
    }
  }

  onTouchMove = (e) => {
    if (e.touches.length === 2 && this._pinch) {
      e.preventDefault()
      const a = e.touches[0]
      const b = e.touches[1]
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
      const cx = (a.clientX + b.clientX) * 0.5
      const cy = (a.clientY + b.clientY) * 0.5
      const wrap = this.wrapRef.current
      const rect = wrap ? wrap.getBoundingClientRect() : { width: 256, height: 256 }
      const w = Math.max(40, rect.width)
      const h = Math.max(40, rect.height)
      this.zoom = clamp(this._pinch.zoom * (d / this._pinch.dist), MPR3D_ZOOM_MIN, MPR3D_ZOOM_MAX)
      this.panX = clamp(this._pinch.panX + (cx - this._pinch.cx) / w * 1.7, -MPR3D_PAN_MAX, MPR3D_PAN_MAX)
      this.panY = clamp(this._pinch.panY - (cy - this._pinch.cy) / h * 1.7, -MPR3D_PAN_MAX, MPR3D_PAN_MAX)
      this.markFastThenIdle()
    }
  }

  onTouchEnd = (e) => {
    if (e.touches.length < 2) this._pinch = null
  }

  stopOrbit = (e) => {
    e.stopPropagation()
    e.preventDefault()
    this._drag = null
  }

  runChip = (e, fn) => {
    e.stopPropagation()
    e.preventDefault()
    this._drag = null
    if (typeof fn === 'function') fn()
  }

  setShading = (shading) => {
    this._shading = shading
    this.setState({ shading })
    this.scheduleDraw(false)
  }

  refreshTf = () => {
    if (!this._glr || !this._glr.setTf) return
    if (this._lookMode === 'clinical') {
      this._glr.setTf(buildClinicalTf(this.props.colorStyle || this._colorStyle || 'anatomico'))
      return
    }
    const palette = this.props.palette || this._palette || 'general'
    const style = this.props.colorStyle || this._colorStyle || 'anatomico'
    this._glr.setTf(buildRegionTf(palette, style))
  }

  setLookMode = (id, opts = {}) => {
    const next = id === 'clinical' ? 'clinical' : 'present'
    this._lookMode = next
    if (next === 'present' && this._shading === 'mip') {
      this._shading = 'dvr'
    }
    this.setState({
      lookMode: next,
      shading: this._shading,
    }, () => {
      this.refreshTf()
      this.scheduleDraw(false)
    })
    if (opts.syncWwwc && this.props.onWwwcFromMpr) {
      this.props.onWwwcFromMpr()
    }
    this.persistSession()
  }

  setDetailFilter = (id) => {
    const hit = DETAIL_FILTERS.find((d) => d.id === id) || DETAIL_FILTERS[0]
    this._detailId = hit.id
    const goClinical = this._lookMode !== 'clinical'
    this.setState({ detailId: hit.id }, () => {
      if (goClinical) this.setLookMode('clinical')
      else this.scheduleDraw(false)
    })
  }

  setLightPreset = (id) => {
    const hit = LIGHT_PRESETS.find((p) => p.id === id)
    this._lightId = hit ? hit.id : 'ne'
    this.setState({ lightId: this._lightId })
    if (this._lookMode !== 'present') this.setLookMode('present')
    else this.scheduleDraw(false)
  }

  setTool = (id) => {
    this._tool = id || 'orbit'
    this.setState({ tool: this._tool })
    if (id === 'peel' && !this.props.decapado && this.props.onToggleDecapado) {
      this.props.onToggleDecapado()
    }
  }

  persistSession = () => {
    saveMpr3dSession({
      lookMode: this._lookMode,
      lightId: this._lightId,
      detailId: this._detailId,
      uiHidden: this.state.uiHidden,
      wwwcLink: !!this.props.wwwcLink,
    })
  }

  onKeyDown = (e) => {
    if (!this.props.volume) return
    const t = e.target
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return
    if (e.metaKey || e.ctrlKey || e.altKey) return
    const k = e.key
    if (k === 'h' || k === 'H') {
      e.preventDefault()
      this.toggleUi()
    } else if (k === 'r' || k === 'R') {
      e.preventDefault()
      this.resetView()
    } else if (k === '1') {
      e.preventDefault()
      this.orientToPlane('axial')
    } else if (k === '2') {
      e.preventDefault()
      this.orientToPlane('coronal')
    } else if (k === '3') {
      e.preventDefault()
      this.orientToPlane('sagittal')
    }
  }

  fitToRoi = (box) => {
    if (!box || !box.centroid) return
    const vol = this.props.volume
    const n = vol && vol.size
    if (!n) return
    const w = mpr3dGridToWorld(box.centroid.x, box.centroid.y, box.centroid.z, n, vol.boxScale)
    this.panX = clamp((w.x - 0.5) * 1.4, -MPR3D_PAN_MAX, MPR3D_PAN_MAX)
    this.panY = clamp((0.5 - w.y) * 1.4, -MPR3D_PAN_MAX, MPR3D_PAN_MAX)
    const dx = (box.max.x - box.min.x) / Math.max(1, n - 1)
    const dy = (box.max.y - box.min.y) / Math.max(1, n - 1)
    const dz = (box.max.z - box.min.z) / Math.max(1, n - 1)
    const span = Math.max(dx, dy, dz, 0.08)
    this.zoom = clamp(0.55 / span, MPR3D_ZOOM_MIN, MPR3D_ZOOM_MAX)
    this.scheduleDraw(false)
  }

  toggleUi = () => {
    this.setState({ uiHidden: !this.state.uiHidden }, this.persistSession)
  }

  capturePng = () => {
    this.scheduleDraw(false)
    const run = () => {
      const gl = this.glCanvasRef.current
      const cpu = this.cpuCanvasRef.current
      const canvas = this._backend === 'cpu' ? cpu : gl
      if (!canvas) return
      const name = `vol3d-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`
      const save = (blob) => {
        if (!blob) return
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = name
        a.click()
        setTimeout(() => URL.revokeObjectURL(url), 2000)
        if (typeof this.props.onCapture === 'function') this.props.onCapture(blob, name)
      }
      if (canvas.toBlob) canvas.toBlob(save, 'image/png')
      else {
        const data = canvas.toDataURL('image/png')
        fetch(data).then((r) => r.blob()).then(save).catch(() => {})
      }
    }
    requestAnimationFrame(() => requestAnimationFrame(run))
  }

  renderToolBar = (tool, gl, shading, wwwcOn) => {
    const lookMode = this.state.lookMode || this._lookMode
    const lightId = this.state.lightId || this._lightId
    return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {VIEW_TOOLS.map((t) => {
        const on = tool === t.id
        const title =
          t.id === 'wwwc'
            ? 'WW/WC: seleccioná y arrastrá en el volumen (↔ centro, ↕ ancho)'
            : t.label
        return (
          <button
            key={t.id}
            type="button"
            title={title}
            onPointerDown={(e) => this.runChip(e, () => this.setTool(t.id))}
            style={chipStyle(
              on,
              on
                ? (t.id === 'wwwc' ? 'rgba(40, 120, 160, 0.9)' : 'rgba(40, 90, 160, 0.82)')
                : 'rgba(20, 20, 20, 0.72)'
            )}
          >
            {t.label}
          </button>
        )
      })}
      <button
        type="button"
        title="Restablecer vista"
        onPointerDown={(e) => this.runChip(e, this.resetView)}
        style={chipStyle(false)}
      >
        Reset
      </button>
      {gl ? (
        <button
          type="button"
          title={shading === 'dvr' ? 'Cambiar a MIP' : 'Cambiar a volumen (DVR)'}
          onPointerDown={(e) => this.runChip(e, () => this.setShading(shading === 'dvr' ? 'mip' : 'dvr'))}
          style={chipStyle(shading === 'dvr', shading === 'dvr' ? 'rgba(40, 90, 160, 0.78)' : 'rgba(20, 20, 20, 0.72)')}
        >
          {shading === 'dvr' ? 'Volumen' : 'MIP'}
        </button>
      ) : null}
      <button
        type="button"
        title={this.state.uiHidden ? 'Mostrar paleta, color y ventana' : 'Ocultar paleta, color y ventana'}
        onPointerDown={(e) => this.runChip(e, this.toggleUi)}
        style={chipStyle(!this.state.uiHidden, this.state.uiHidden ? 'rgba(50, 50, 50, 0.65)' : 'rgba(70, 70, 70, 0.75)')}
      >
        {this.state.uiHidden ? 'Más' : 'Menos'}
      </button>
      <button
        type="button"
        title="Capas, máscara (Modo B) y cuantificación ROI"
        onPointerDown={(e) => this.runChip(e, () => this.setState({ layersOpen: !this.state.layersOpen }))}
        style={chipStyle(!!this.state.layersOpen, this.state.layersOpen ? 'rgba(40, 90, 70, 0.88)' : 'rgba(20, 20, 20, 0.72)')}
      >
        Capas
      </button>
      <button
        type="button"
        title="Guardar captura PNG del volumen 3D (informe, junta, demo)"
        onPointerDown={(e) => this.runChip(e, this.capturePng)}
        style={chipStyle(false, 'rgba(40, 80, 70, 0.78)')}
      >
        Captura
      </button>
      {this.props.onToggleMaximize ? (
        <button
          type="button"
          title="Maximizar / restaurar 3D (doble toque)"
          onPointerDown={(e) => this.runChip(e, this.props.onToggleMaximize)}
          style={chipStyle(!!this.props.maximized, 'rgba(50, 50, 80, 0.8)')}
        >
          {this.props.maximized ? 'Rest.' : 'Max'}
        </button>
      ) : null}
      {this.props.onMeasureTool ? (
        <button
          type="button"
          title="Longitud en el MPR reconstruido"
          onPointerDown={(e) => this.runChip(e, () => this.props.onMeasureTool('Length'))}
          style={chipStyle(false)}
        >
          Medir
        </button>
      ) : null}
      {this.props.onMeasureTool ? (
        <button
          type="button"
          title="Ángulo en el MPR reconstruido"
          onPointerDown={(e) => this.runChip(e, () => this.props.onMeasureTool('Angle'))}
          style={chipStyle(false)}
        >
          Ángulo
        </button>
      ) : null}
      {this.props.onQuickPreset ? (
        <button
          type="button"
          title="Preset clínico de lesión (un toque)"
          onPointerDown={(e) => this.runChip(e, () => this.props.onQuickPreset('lesion'))}
          style={chipStyle(false, 'rgba(90, 50, 40, 0.8)')}
        >
          Lesión
        </button>
      ) : null}
    </div>
    {gl ? (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span
          style={{
            fontSize: 10,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.55)',
            padding: '0 2px',
            pointerEvents: 'none',
          }}
        >
          Modo
        </span>
        {LOOK_MODES.map((m) => {
          const on = lookMode === m.id
          return (
            <button
              key={m.id}
              type="button"
              title={
                m.id === 'clinical'
                  ? 'Clínico: aspecto cercano al MPR (gris + WW/WC). Ideal con punto mira.'
                  : 'Presentación: modelo 3D con sombras e iluminación.'
              }
              onPointerDown={(e) => this.runChip(e, () => this.setLookMode(m.id, {
                syncWwwc: m.id === 'clinical',
              }))}
              style={chipStyle(
                on,
                on
                  ? (m.id === 'clinical' ? 'rgba(50, 110, 140, 0.9)' : 'rgba(140, 90, 40, 0.88)')
                  : 'rgba(20, 20, 20, 0.72)'
              )}
            >
              {m.label}
            </button>
          )
        })}
        {lookMode === 'present' ? (
          <>
            <span
              style={{
                fontSize: 10,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.55)',
                padding: '0 2px',
                pointerEvents: 'none',
              }}
            >
              Luz
            </span>
            {LIGHT_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                title={`Iluminación ${p.label}`}
                onPointerDown={(e) => this.runChip(e, () => this.setLightPreset(p.id))}
                style={chipStyle(lightId === p.id, lightId === p.id ? 'rgba(160, 110, 50, 0.85)' : 'rgba(20, 20, 20, 0.72)')}
              >
                {p.label}
              </button>
            ))}
          </>
        ) : (
          <>
            <span
              style={{
                fontSize: 10,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.55)',
                padding: '0 2px',
                pointerEvents: 'none',
              }}
            >
              Detalle
            </span>
            {DETAIL_FILTERS.map((d) => {
              const on = (this.state.detailId || this._detailId) === d.id
              return (
                <button
                  key={d.id}
                  type="button"
                  title={
                    d.id === 'soft'
                      ? 'Sin realce (más suave)'
                      : d.id === 'grain'
                        ? 'Unsharp fuerte: grano de parénquima más visible'
                        : 'Unsharp: más detalle, cercano al MPR'
                  }
                  onPointerDown={(e) => this.runChip(e, () => this.setDetailFilter(d.id))}
                  style={chipStyle(on, on ? 'rgba(50, 110, 140, 0.9)' : 'rgba(20, 20, 20, 0.72)')}
                >
                  {d.label}
                </button>
              )
            })}
          </>
        )}
      </div>
    ) : null}
    </div>
    )
  }

  applyPalette = (id, colorStyle) => {
    this._palette = id || 'general'
    if (colorStyle) this._colorStyle = colorStyle
    this.setState({ palette: this._palette, colorStyle: this._colorStyle })
    this.refreshTf()
    this.scheduleDraw(false)
  }

  applyColorStyle = (id) => {
    this._colorStyle = id || 'anatomico'
    this.setState({ colorStyle: this._colorStyle })
    this.refreshTf()
    this.scheduleDraw(false)
  }

  setPalette = (id) => {
    const next = id || 'general'
    const inferred = this.props.inferredPalette || 'general'
    const restoreClinical = !!(
      this.props.markOn &&
      (next === inferred || next === 'cerebro')
    )
    if (restoreClinical) {
      this.setLookMode('clinical', { syncWwwc: true })
    } else if (this._lookMode === 'clinical') {
      this.setLookMode('present')
    }
    if (this.props.onPalette) {
      this.props.onPalette(next, { keepWwwc: restoreClinical })
      return
    }
    this.applyPalette(next)
  }

  setColorStyle = (id) => {
    const next = id || 'anatomico'
    this._colorStyle = next
    this.setState({ colorStyle: next })
    if (this.props.onColorStyle) {
      this.props.onColorStyle(next)
    }
    this.refreshTf()
    this.scheduleDraw(false)
  }

  nudgeWwwc = (which, dir) => {
    if (!this.props.onWwwc) return
    const ww = Number(this.props.ww)
    const wc = Number(this.props.wc)
    const curW = ww > 0 ? ww : 400
    const curC = Number.isFinite(wc) ? wc : 40
    if (which === 'ww') {
      const step = curW >= 800 ? 100 : curW >= 200 ? 40 : 10
      this.props.onWwwc(curW + dir * step, curC)
    } else {
      const step = Math.max(5, Math.round(curW * 0.05))
      this.props.onWwwc(curW, curC + dir * step)
    }
  }

  resetView = () => {
    this.yaw = MPR3D_DEFAULT_YAW
    this.pitch = MPR3D_DEFAULT_PITCH
    this.zoom = MPR3D_DEFAULT_ZOOM
    this.panX = 0
    this.panY = 0
    this.mirrorX = false
    this.mirrorY = false
    this.scheduleDraw(false)
  }

  orientToPlane = (plane) => {
    const p = String(plane || '').toLowerCase()
    this.mirrorX = false
    this.mirrorY = false
    // Cámara desde el hemicorte vacío. Axial: desde los pies (radiológico:
    // A arriba, R a la izquierda), igual que el MPR. Sin mirrorY: la pantalla
    // ya tiene +Up arriba.
    if (p === 'axial' || p === 'z') {
      this.yaw = 0.08
      this.pitch = -1.12
    } else if (p === 'sagittal' || p === 'x') {
      this.yaw = 1.57
      this.pitch = 0.08
    } else {
      this.yaw = 0.08
      this.pitch = 0.06
    }
    this.panX = 0
    this.panY = 0
    this.scheduleDraw(false)
  }

  renderOrientationRose = () => {
    const lab = vol3dOrientationLabels(this.yaw, this.pitch, this.mirrorX, this.mirrorY)
    const edge = (pos, text) => {
      const { key, ...stylePos } = pos
      return (
      <div
        key={key}
        style={{
          position: 'absolute',
          zIndex: 5,
          pointerEvents: 'none',
          color: '#cfe8ff',
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 1,
          textShadow: '0 0 4px #000, 0 1px 2px #000',
          ...stylePos,
        }}
      >
        {text}
      </div>
      )
    }
    return (
      <div style={{ position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'none' }}>
        {edge({ key: 'ori-top', top: 8, left: '50%', transform: 'translateX(-50%)' }, lab.top)}
        {edge({ key: 'ori-bottom', bottom: 72, left: '50%', transform: 'translateX(-50%)' }, lab.bottom)}
        {edge({ key: 'ori-left', left: 8, top: '45%' }, lab.left)}
        {edge({ key: 'ori-right', right: 8, top: '45%' }, lab.right)}
        <div
          style={{
            position: 'absolute',
            right: 10,
            bottom: 78,
            fontSize: 10,
            color: 'rgba(200,230,255,0.75)',
            textShadow: '0 1px 2px #000',
          }}
        >
          cerca {lab.near} · lejos {lab.far}
        </div>
      </div>
    )
  }

  render() {
    const building = !!this.props.building
    const hasVol = !!(this.props.volume && this.props.volume.data)
    const gl = this.state.backend === 'gl'
    const shading = this.state.shading || this._shading
    const tool = this.state.tool || this._tool || 'orbit'
    const wwwcOn = tool === 'wwwc'
    return (
      <div
        ref={this.wrapRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          background: '#000',
          touchAction: 'none',
          overflow: 'hidden',
          userSelect: 'none',
          cursor: wwwcOn ? 'ns-resize' : tool === 'pan' ? 'move' : tool === 'zoom' ? 'zoom-in' : tool === 'peel' ? 'row-resize' : 'grab',
        }}
      >
        <canvas
          ref={this.glCanvasRef}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            display: this.state.backend === 'cpu' ? 'none' : 'block',
            width: '100%',
            height: '100%',
            imageRendering: 'auto',
          }}
        />
        <canvas
          ref={this.cpuCanvasRef}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            display: this.state.backend === 'cpu' ? 'block' : 'none',
            width: '100%',
            height: '100%',
            imageRendering: 'auto',
          }}
        />
        {this.props.meshUrl || this.props.isolateBox ? (
          <Mpr3dMeshOverlay
            ref={this.meshRef}
            url={this.props.meshUrl}
            volume={this.props.volume}
            isolateBox={this.props.isolateBox}
            yaw={this.yaw}
            pitch={this.pitch}
            zoom={this.zoom}
          />
        ) : null}
        {hasVol && !building ? this.renderOrientationRose() : null}
        {hasVol && !building && this.state.layersOpen ? (
          <Mpr3dLayersPanel
            hasMask={!!this.props.mask}
            baseVisible={this.props.baseVisible !== false}
            maskOn={!!this.props.maskOn}
            quant={this.props.quant}
            error={this.props.maskError}
            segUrl={this.props.segUrl}
            onToggleBase={(e) => this.props.onToggleBase && this.runChip(e, this.props.onToggleBase)}
            onToggleMask={(e) => this.props.onToggleMask && this.runChip(e, this.props.onToggleMask)}
            onIsolate={(e) => this.props.onIsolateStructure && this.runChip(e, this.props.onIsolateStructure)}
            onLoadSegFile={this.props.onLoadMaskFile}
          />
        ) : null}
        {building || !hasVol ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#bbb',
              fontSize: 13,
              pointerEvents: 'none',
            }}
          >
            {this.props.buildingLabel || 'Preparando 3D…'}
          </div>
        ) : null}
        {hasVol && !building ? (
          <div
            data-mpr3d-ui="1"
            style={{
              position: 'absolute',
              left: 8,
              bottom: 8,
              right: 8,
              maxWidth: '100%',
              zIndex: 6,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              pointerEvents: 'auto',
            }}
          >
            {this.renderToolBar(tool, gl, shading, wwwcOn)}
            {wwwcOn ? (
              <div
                style={{
                  fontSize: 11,
                  color: 'rgba(140, 220, 255, 0.95)',
                  padding: '2px 4px',
                  pointerEvents: 'none',
                }}
              >
                Arrastrá en el 3D: ↔ WC · ↕ WW
              </div>
            ) : null}
            {!this.state.uiHidden && gl ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span
                  style={{
                    fontSize: 10,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.55)',
                    padding: '0 2px',
                    pointerEvents: 'none',
                  }}
                >
                  Paleta
                </span>
                {this.props.inferredPalette ? (
                  <span
                    style={{
                      fontSize: 10,
                      color: 'rgba(255,255,255,0.45)',
                      pointerEvents: 'none',
                    }}
                  >
                    {mpr3dPresetLabel(this.props.inferredPalette)}
                  </span>
                ) : null}
                {MPR3D_REGION_PALETTES.map((p) => {
                  const current = this.props.palette || this.state.palette || this._palette
                  const on = current === p.id
                  const auto = this.props.inferredPalette === p.id
                  return (
                    <button
                      key={p.id}
                      type="button"
                      title={auto ? `${p.label} (detectado en el DICOM)` : `Textura ${p.label.toLowerCase()}`}
                      onPointerDown={(e) => this.runChip(e, () => this.setPalette(p.id))}
                      style={chipStyle(on, on ? 'rgba(60, 120, 90, 0.82)' : 'rgba(20, 20, 20, 0.72)')}
                    >
                      {p.label}{auto ? ' · auto' : ''}
                    </button>
                  )
                })}
              </div>
            ) : null}
            {!this.state.uiHidden && gl ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span
                  style={{
                    fontSize: 10,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.55)',
                    padding: '0 2px',
                    pointerEvents: 'none',
                  }}
                >
                  Color
                </span>
                {MPR3D_COLOR_STYLES.map((c) => {
                  const current = this.props.colorStyle || this.state.colorStyle || this._colorStyle || 'anatomico'
                  const on = current === c.id
                  return (
                    <button
                      key={c.id}
                      type="button"
                      title={`CLUT ${c.label}`}
                      onPointerDown={(e) => this.runChip(e, () => this.setColorStyle(c.id))}
                      style={{
                        ...chipStyle(on, on ? 'rgba(60, 90, 140, 0.85)' : 'rgba(20, 20, 20, 0.72)'),
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          background: c.swatch,
                          border: '1px solid rgba(255,255,255,0.35)',
                          flexShrink: 0,
                        }}
                      />
                      {c.label}
                    </button>
                  )
                })}
              </div>
            ) : null}
            {!this.state.uiHidden && gl ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span
                  style={{
                    fontSize: 10,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.55)',
                    padding: '0 2px',
                    pointerEvents: 'none',
                  }}
                >
                  Ventana
                </span>
                <button
                  type="button"
                  title="Activar WW/WC y arrastrar en el volumen"
                  onPointerDown={(e) => this.runChip(e, () => this.setTool('wwwc'))}
                  style={{
                    fontSize: 11,
                    color: wwwcOn ? 'rgba(120, 200, 255, 0.95)' : 'rgba(255,255,255,0.7)',
                    padding: '2px 8px',
                    fontVariantNumeric: 'tabular-nums',
                    ...chipStyle(wwwcOn, wwwcOn ? 'rgba(40, 120, 160, 0.85)' : 'rgba(20, 20, 20, 0.55)'),
                  }}
                >
                  {Math.round(Number(this.props.ww) || 0)}/{Math.round(Number(this.props.wc) || 0)}
                </button>
                <button
                  type="button"
                  title="Bajar WW"
                  onPointerDown={(e) => this.runChip(e, () => this.nudgeWwwc('ww', -1))}
                  style={chipStyle(false)}
                >
                  W−
                </button>
                <button
                  type="button"
                  title="Subir WW"
                  onPointerDown={(e) => this.runChip(e, () => this.nudgeWwwc('ww', 1))}
                  style={chipStyle(false)}
                >
                  W+
                </button>
                <button
                  type="button"
                  title="Bajar WC"
                  onPointerDown={(e) => this.runChip(e, () => this.nudgeWwwc('wc', -1))}
                  style={chipStyle(false)}
                >
                  C−
                </button>
                <button
                  type="button"
                  title="Subir WC"
                  onPointerDown={(e) => this.runChip(e, () => this.nudgeWwwc('wc', 1))}
                  style={chipStyle(false)}
                >
                  C+
                </button>
                {this.props.onToggleWwwcLink ? (
                  <button
                    type="button"
                    title="Vincular WW/WC entre MPR y 3D"
                    onPointerDown={(e) => this.runChip(e, this.props.onToggleWwwcLink)}
                    style={chipStyle(!!this.props.wwwcLink, this.props.wwwcLink ? 'rgba(40, 120, 90, 0.88)' : 'rgba(90, 70, 40, 0.75)')}
                  >
                    {this.props.wwwcLink ? 'Ventana MPR↔3D' : 'Ventana libre'}
                  </button>
                ) : this.props.onWwwcFromMpr ? (
                  <button
                    type="button"
                    title="Copiar WW/WC del viewport MPR activo"
                    onPointerDown={(e) => this.runChip(e, this.props.onWwwcFromMpr)}
                    style={chipStyle(false, 'rgba(90, 70, 40, 0.75)')}
                  >
                    Desde MPR
                  </button>
                ) : null}
              </div>
            ) : null}
            {this.props.decapado ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {CLIP_AXES.map((ax) => (
                  <button
                    key={ax.id}
                    type="button"
                    title={`Decapar en ${ax.label.toLowerCase()}`}
                    onPointerDown={(e) => this.runChip(e, () => this.props.onClipAxis && this.props.onClipAxis(ax.id))}
                    style={chipStyle(
                      (this.props.clipAxis || 'z') === ax.id,
                      (this.props.clipAxis || 'z') === ax.id
                        ? 'rgba(180, 110, 40, 0.85)'
                        : 'rgba(20, 20, 20, 0.72)'
                    )}
                  >
                    {ax.label}
                  </button>
                ))}
                {this.props.onClipNudge ? (
                  <button
                    type="button"
                    title="Retroceder el plano"
                    onPointerDown={(e) => this.runChip(e, () => this.props.onClipNudge(-1))}
                    style={chipStyle(false)}
                  >
                    −
                  </button>
                ) : null}
                {this.props.onClipNudge ? (
                  <button
                    type="button"
                    title="Avanzar el plano"
                    onPointerDown={(e) => this.runChip(e, () => this.props.onClipNudge(1))}
                    style={chipStyle(false)}
                  >
                    +
                  </button>
                ) : null}
                {this.props.onToggleKeep ? (
                  <button
                    type="button"
                    title="Cambiar el hemicorte que se conserva"
                    onPointerDown={(e) => this.runChip(e, this.props.onToggleKeep)}
                    style={chipStyle(false)}
                  >
                    Cambiar lado
                  </button>
                ) : null}
                {this.props.onToggleDecapado ? (
                  <button
                    type="button"
                    title="Volver al volumen completo"
                    onPointerDown={(e) => this.runChip(e, this.props.onToggleDecapado)}
                    style={chipStyle(true)}
                  >
                    Completo
                  </button>
                ) : null}
                {this.props.onSlab ? (
                  <button
                    type="button"
                    title="Hemicorte: conserva un lado del plano (anatomía / presentación)"
                    onPointerDown={(e) => this.runChip(e, () => this.props.onSlab(0))}
                    style={chipStyle(!(Number(this.props.slab) > 0))}
                  >
                    Hemi
                  </button>
                ) : null}
                {this.props.onSlabNudge ? (
                  <button
                    type="button"
                    title="Espesor fino (~banda estrecha alrededor del plano de la mira)"
                    onPointerDown={(e) => this.runChip(e, () => this.props.onSlab && this.props.onSlab(4))}
                    style={chipStyle((Number(this.props.slab) || 0) > 0 && (Number(this.props.slab) || 0) <= 5)}
                  >
                    Fino
                  </button>
                ) : null}
                {this.props.onSlabNudge ? (
                  <button
                    type="button"
                    title="Espesor medio (5–10 mm aprox.) para lesión / ACV"
                    onPointerDown={(e) => this.runChip(e, () => this.props.onSlab && this.props.onSlab(8))}
                    style={chipStyle((Number(this.props.slab) || 0) > 5 && (Number(this.props.slab) || 0) <= 12)}
                  >
                    Medio
                  </button>
                ) : null}
                {this.props.onSlabNudge ? (
                  <button
                    type="button"
                    title="Espesor grueso (más contexto alrededor del plano)"
                    onPointerDown={(e) => this.runChip(e, () => this.props.onSlab && this.props.onSlab(18))}
                    style={chipStyle((Number(this.props.slab) || 0) > 12)}
                  >
                    Grueso
                  </button>
                ) : null}
                {this.props.onSlabNudge ? (
                  <button
                    type="button"
                    title="Reducir espesor del slab"
                    onPointerDown={(e) => this.runChip(e, () => this.props.onSlabNudge(-1))}
                    style={chipStyle(false)}
                  >
                    Esp−
                  </button>
                ) : null}
                {this.props.onSlabNudge ? (
                  <button
                    type="button"
                    title="Aumentar espesor del slab"
                    onPointerDown={(e) => this.runChip(e, () => this.props.onSlabNudge(1))}
                    style={chipStyle(false)}
                  >
                    Esp+
                  </button>
                ) : null}
                {(Number(this.props.slab) || 0) > 0 ? (
                  <span
                    style={{
                      fontSize: 11,
                      color: 'rgba(255,220,160,0.9)',
                      padding: '2px 6px',
                      pointerEvents: 'none',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                    title="Semiespesor del slab en vóxeles de la grilla 3D"
                  >
                    ±{Math.round(Number(this.props.slab) || 0)}
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.5)',
                      padding: '2px 6px',
                      pointerEvents: 'none',
                    }}
                  >
                    Hemicorte
                  </span>
                )}
              </div>
            ) : null}
            {!this.state.uiHidden ? (
              <div
                style={{
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.5)',
                  pointerEvents: 'none',
                }}
              >
                {this.props.decapado
                  ? 'Decapar: arrastre mueve el plano y el MPR · Fino/Medio/Grueso: espesor · Captura: PNG'
                  : 'Arrastrar: orbitar · rueda: zoom · H UI · R reset · 1/2/3 planos · doble toque: max'}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    )
  }
}
